import React, { memo, useCallback, useState } from "react";
import {
  Card,
  CardMedia,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Fade,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  Edit,
  Delete,
  Visibility,
} from "@mui/icons-material";
import { Item } from "@catalog/types";
import { useImageDimensions } from "../../hooks/useImageDimensions";

// Status color mapping - moved outside component to prevent recreation
const statusColors = {
  staged: "primary",
  in_storage: "success",
  unavailable: "error",
  default: "default",
} as const;

// Helper function to get status color - memoized
const getStatusColor = (status: string) =>
  statusColors[status as keyof typeof statusColors] || statusColors.default;

interface ItemCardProps {
  item: Item;
  onEdit?: (item: Item) => void;
  onDelete?: (item: Item) => void;
  onView?: (item: Item) => void;
  isSelected?: boolean;
}

// Predefined aspect ratios for consistent layout (height/width ratios)
// Includes more vertical options for better variety
const ASPECT_RATIOS = [0.5, 0.67, 0.75, 0.9, 1.0, 1.2, 1.33, 1.5, 1.78, 2.0];
const NO_IMAGE_RATIOS = [0.7, 0.9, 1.0, 1.2, 1.4];

// Generate consistent aspect ratio based on item ID - memoized outside component
const getAspectRatio = (itemId: string, hasImage: boolean) => {
  const ratios = hasImage ? ASPECT_RATIOS : NO_IMAGE_RATIOS;
  const hash = itemId.split("").reduce((a, b) => a + b.charCodeAt(0), 0);
  return ratios[hash % ratios.length];
};

const ItemCard = memo<ItemCardProps>(
  ({ item, onEdit, onDelete, onView, isSelected }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isHovered, setIsHovered] = useState(false);
    const open = Boolean(anchorEl);

    const hasImage = Boolean(item.photo_url && item.photo_url.length > 0);
    const firstImageUrl = hasImage ? item.photo_url?.[0] : undefined;

    // Get actual image dimensions
    const { isLoading: imageLoading, hasError } =
      useImageDimensions(firstImageUrl);

    // Calculate minimum height for cards without images - memoized
    const minimumHeight = React.useMemo(() => {
      if (!hasImage || hasError) {
        // Only set height for cards without images
        const fallbackAspectRatio = getAspectRatio(item.id, hasImage);
        return Math.max(180, Math.min(400, 280 * fallbackAspectRatio));
      }
      return undefined; // Let image cards size naturally
    }, [hasImage, hasError, item.id]);

    // Memoized handlers to prevent unnecessary re-renders
    const handleMenuClick = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
      },
      []
    );

    const handleMenuClose = useCallback(() => {
      setAnchorEl(null);
    }, []);

    const handleEdit = useCallback(() => {
      onEdit?.(item);
      handleMenuClose();
    }, [onEdit, item, handleMenuClose]);

    const handleDelete = useCallback(() => {
      onDelete?.(item);
      handleMenuClose();
    }, [onDelete, item, handleMenuClose]);

    const handleView = useCallback(() => {
      onView?.(item);
      handleMenuClose();
    }, [onView, item, handleMenuClose]);

    const handleCardClick = useCallback(() => {
      onView?.(item);
    }, [onView, item]);

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleCardClick();
        }
      },
      [handleCardClick]
    );

    // Mobile-specific handlers that disable hover
    const handleMouseEnter = useCallback(() => {
      if (!isMobile) {
        setIsHovered(true);
      }
    }, [isMobile]);

    const handleMouseLeave = useCallback(() => {
      if (!isMobile) {
        setIsHovered(false);
      }
    }, [isMobile]);

    return (
      <Card
        role="article"
        aria-label={`${item.name} - €${item.purchase_price.toLocaleString()}`}
        tabIndex={0}
        sx={{
          cursor: "pointer",
          transition: isMobile
            ? "none" // Disable transitions on mobile
            : "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          transform:
            isHovered && !isMobile ? "translateY(-8px)" : "translateY(0)",
          boxShadow: isSelected
            ? (theme) =>
                theme.palette.mode === "dark"
                  ? "0 0 0 2px #0A84FF, 0 20px 40px rgba(10, 132, 255, 0.25)"
                  : "0 0 0 2px #007AFF, 0 20px 40px rgba(0, 122, 255, 0.15)"
            : isHovered && !isMobile
              ? (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 20px 40px rgba(0, 0, 0, 0.4), 0 8px 16px rgba(0, 0, 0, 0.3)"
                    : "0 20px 40px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.08)"
              : (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                    : "0 4px 12px rgba(0, 0, 0, 0.05)",
          borderRadius: 1,
          overflow: "hidden",
          position: "relative",
          border: isSelected
            ? (theme) =>
                theme.palette.mode === "dark"
                  ? "2px solid #0A84FF"
                  : "2px solid #007AFF"
            : (theme) =>
                theme.palette.mode === "dark"
                  ? "1px solid rgba(240, 246, 252, 0.1)"
                  : "1px solid rgba(255, 255, 255, 0.2)",
          backdropFilter: "blur(10px)",
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(22, 27, 34, 0.8)"
              : "rgba(255, 255, 255, 0.8)",
          height: minimumHeight, // Only applies to cards without images
          // Add subtle loading animation while image loads
          ...(hasImage &&
            imageLoading && {
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "3px",
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? "linear-gradient(90deg, transparent, #0A84FF, transparent)"
                    : "linear-gradient(90deg, transparent, #007AFF, transparent)",
                animation: "loading-shimmer 1.5s infinite",
                zIndex: 10,
              },
            }),
          "@keyframes loading-shimmer": {
            "0%": { transform: "translateX(-100%)" },
            "100%": { transform: "translateX(100%)" },
          },
          // Only apply hover styles on non-mobile devices
          ...(!isMobile && {
            "&:hover .card-overlay": {
              opacity: 1,
            },
            "&:hover .action-buttons": {
              opacity: 1,
            },
            "&:hover .item-info": {
              opacity: 1,
              transform: "translateY(0)",
            },
          }),
        }}
        onClick={handleCardClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Full Card Image */}
        <CardMedia
          className="card-media"
          sx={{
            width: "100%",
            backgroundColor: "background.default",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            // For cards with images, let the image determine the height
            // For cards without images, use the full height
            height: hasImage && !hasError ? "auto" : "100%",
          }}
        >
          {hasImage && !hasError ? (
            <img
              src={item.photo_url?.[0] || ""}
              alt={item.name}
              loading="lazy"
              onError={(e) => {
                console.warn(
                  `Failed to load image for item ${item.name}:`,
                  item.photo_url?.[0]
                );
                e.currentTarget.style.display = "none";
              }}
              style={{
                width: "100%",
                height: "auto", // Let image determine its own height
                maxHeight: "800px", // Reasonable maximum to prevent extremely tall cards
                objectFit: "contain", // No cropping - show full image
                transition: isMobile
                  ? "opacity 0.3s ease, filter 0.3s ease" // No transform on mobile
                  : "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease, filter 0.3s ease",
                opacity: imageLoading ? 0.7 : 1,
                filter: imageLoading ? "blur(1px)" : "none",
              }}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                height: "100%",
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(30, 36, 47, 0.6)"
                    : "#F8F9FA",
              }}
            >
              {/* No image content */}
              <Typography
                variant="h1"
                sx={{
                  fontSize: "4rem",
                  opacity: 0.3,
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "linear-gradient(135deg, #0A84FF 0%, #64B5F6 100%)"
                      : "linear-gradient(135deg, #007AFF 0%, #5AC8FA 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                📦
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ opacity: 0.6, fontWeight: 500 }}
              >
                No Image
              </Typography>
            </Box>
          )}

          {/* Overlay with actions - hidden on mobile for clean appearance */}
          <Box
            className="card-overlay"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: (theme) =>
                theme.palette.mode === "dark"
                  ? "linear-gradient(180deg, rgba(13,17,23,0.1) 0%, rgba(13,17,23,0.3) 50%, rgba(13,17,23,0.8) 100%)"
                  : "linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.8) 100%)",
              opacity: 0, // Hidden by default
              transition: isMobile ? "none" : "opacity 0.3s ease",
              display: isMobile ? "none" : "flex", // Completely hidden on mobile
              flexDirection: "column",
              justifyContent: "space-between",
              p: 2,
            }}
          >
            {/* Top section with badges and menu */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              {/* Category and Merchant badges */}
              <Stack direction="row" spacing={0.5}>
                {item.category && (
                  <Chip
                    label={item.category.name}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.65rem",
                      fontWeight: 500,
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 152, 0, 0.85)"
                          : "rgba(255, 152, 0, 0.9)",
                      color: "white",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      boxShadow: (theme) =>
                        theme.palette.mode === "dark"
                          ? "0 2px 8px rgba(0,0,0,0.4)"
                          : "0 2px 8px rgba(0,0,0,0.2)",
                      "& .MuiChip-label": {
                        px: 1,
                      },
                    }}
                  />
                )}
                {item.merchant && (
                  <Chip
                    label={item.merchant.name}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.65rem",
                      fontWeight: 500,
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(10, 132, 255, 0.85)"
                          : "rgba(21, 101, 192, 0.9)",
                      color: "white",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      boxShadow: (theme) =>
                        theme.palette.mode === "dark"
                          ? "0 2px 8px rgba(0,0,0,0.4)"
                          : "0 2px 8px rgba(0,0,0,0.2)",
                      "& .MuiChip-label": {
                        px: 1,
                      },
                    }}
                  />
                )}
              </Stack>

              {/* Menu button - always visible on mobile */}
              <IconButton
                size="small"
                onClick={handleMenuClick}
                aria-label={`More actions for ${item.name}`}
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(22, 27, 34, 0.9)"
                      : "rgba(255, 255, 255, 0.9)",
                  color: (theme) =>
                    theme.palette.mode === "dark" ? "white" : "text.primary",
                  "&:hover": {
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(30, 36, 47, 1)"
                        : "white",
                  },
                  // Ensure button is visible and accessible on mobile
                  opacity: isMobile ? 1 : "inherit",
                  zIndex: 10,
                }}
              >
                <MoreVertIcon />
              </IconButton>
            </Box>

            {/* Bottom section with item info and actions */}
            <Box>
              {/* Item Information */}
              <Box
                className="item-info"
                sx={{
                  mb: 1.5,
                  opacity: isMobile ? 1 : 0, // Always visible on mobile
                  transform: isMobile ? "translateY(0)" : "translateY(20px)",
                  transition: isMobile
                    ? "none"
                    : "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 0.5,
                    color: "white",
                    textShadow: (theme) =>
                      theme.palette.mode === "dark"
                        ? "0 2px 8px rgba(0,0,0,0.5)"
                        : "0 2px 8px rgba(0,0,0,0.3)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: "0.95rem",
                  }}
                >
                  {item.name}
                </Typography>

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={0.5}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "white",
                      textShadow: (theme) =>
                        theme.palette.mode === "dark"
                          ? "0 2px 8px rgba(0,0,0,0.5)"
                          : "0 2px 8px rgba(0,0,0,0.3)",
                      fontSize: "1.1rem",
                    }}
                  >
                    €{item.purchase_price.toLocaleString()}
                  </Typography>
                  <Chip
                    label={item.status.replace("_", " ")}
                    size="small"
                    color={getStatusColor(item.status) as any}
                    sx={{
                      fontWeight: 500,
                      textTransform: "capitalize",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                      fontSize: "0.65rem",
                      height: 18,
                    }}
                  />
                </Stack>

                {item.description && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255, 255, 255, 0.8)",
                      textShadow: "0 1px 4px rgba(0,0,0,0.3)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "0.75rem",
                      display: "block",
                    }}
                  >
                    {item.description}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </CardMedia>

        {/* Menu */}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          TransitionComponent={Fade}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          PaperProps={{
            sx: {
              borderRadius: 1,
              minWidth: 120,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            },
          }}
        >
          <MenuItem onClick={handleView} sx={{ gap: 1 }}>
            <Visibility fontSize="small" />
            View
          </MenuItem>
          <MenuItem onClick={handleEdit} sx={{ gap: 1 }}>
            <Edit fontSize="small" />
            Edit
          </MenuItem>
          <MenuItem onClick={handleDelete} sx={{ gap: 1, color: "error.main" }}>
            <Delete fontSize="small" />
            Delete
          </MenuItem>
        </Menu>
      </Card>
    );
  }
);

ItemCard.displayName = "ItemCard";

export default ItemCard;
